import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const CustomerDatePicker = ({
  selectedDate,
  onDateSelect,
  blockedDates = [],
  serviceId = null,
  serviceIds = null,
  isSubmitting = false
}) => {
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [availabilityError, setAvailabilityError] = useState('');

  // Load unavailable dates when component mounts or services change
  useEffect(() => {
    const hasService = serviceId || (serviceIds && serviceIds.length > 0);
    if (hasService) {
      loadUnavailableDates();
    }
  }, [blockedDates, serviceId, serviceIds]);

  // API function for availability checking
  const loadUnavailableDates = async () => {
    try {
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 3 months ahead
      
      let serviceParam = '';
      if (serviceIds && serviceIds.length > 1) {
        serviceParam = `&service_ids=${serviceIds.join(',')}`;
      } else if (serviceIds && serviceIds.length === 1) {
        serviceParam = `&service_id=${serviceIds[0]}`;
      } else if (serviceId) {
        serviceParam = `&service_id=${serviceId}`;
      }
      const response = await fetch(`/scheduler/api/availability.php?start_date=${startDate}&end_date=${endDate}${serviceParam}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        const fullyBookedDates = result.data?.fully_booked_dates || [];
        
        // Combine blocked dates with fully booked dates
        const allUnavailableDates = [
          ...blockedDates.map(bd => bd.date),
          ...fullyBookedDates
        ];
        
        setUnavailableDates([...new Set(allUnavailableDates)]); // Remove duplicates
        setAvailabilityError('');
        
        console.log('DatePicker: Loaded unavailable dates:', allUnavailableDates);
      } else {
        console.error('Failed to load available dates:', result.error);
        setAvailabilityError('Failed to load date availability');
      }
    } catch (error) {
      console.error('Error loading available dates:', error);
      setAvailabilityError('Error checking date availability');
      
      // Fallback: use only blocked dates
      setUnavailableDates(blockedDates.map(bd => bd.date));
    }
  };

  // Calendar utility functions
  const formatDateForDB = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateUnavailable = (dateString) => {
    return unavailableDates.includes(dateString);
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const generateCalendar = () => {
    const year = currentCalendarDate.getFullYear();
    const month = currentCalendarDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    
    const calendar = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      calendar.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendar.push(date);
    }
    
    return calendar;
  };

  const navigateMonth = (direction) => {
    setCurrentCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    const dateStr = formatDateForDB(date);
    
    if (isPastDate(date) || isDateUnavailable(dateStr)) {
      return; // Don't allow selection of unavailable dates
    }
    
    onDateSelect(dateStr);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calendar = generateCalendar();
  const monthYear = currentCalendarDate.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Date *</h3>
      
      {availabilityError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <p className="text-yellow-800 text-sm">{availabilityError}</p>
        </div>
      )}
      
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-4">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="p-2 hover:bg-gray-200 rounded-lg"
          disabled={isSubmitting}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h4 className="text-lg font-semibold text-gray-800">{monthYear}</h4>
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="p-2 hover:bg-gray-200 rounded-lg"
          disabled={isSubmitting}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-medium text-gray-600 py-2 text-sm">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {calendar.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-10"></div>;
          }

          const dateStr = formatDateForDB(date);
          const isPast = isPastDate(date);
          const isUnavailable = isDateUnavailable(dateStr);
          const isSelected = selectedDate === dateStr;
          const isToday = dateStr === new Date().toISOString().split('T')[0];

          let className = 'h-10 rounded-lg text-sm font-medium transition-all cursor-pointer flex items-center justify-center ';
          
          if (isPast || isUnavailable) {
            className += 'text-gray-300 bg-gray-100 cursor-not-allowed';
          } else if (isSelected) {
            className += 'bg-green-500 text-white';
          } else if (isToday) {
            className += 'bg-blue-100 text-blue-800 hover:bg-blue-200';
          } else {
            className += 'text-gray-700 hover:bg-green-100';
          }

          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => handleDateClick(date)}
              disabled={isPast || isUnavailable || isSubmitting}
              className={className}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Calendar Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-4">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded"></div>
          <span>Today</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-100 rounded"></div>
          <span>Unavailable</span>
        </div>
      </div>

      {selectedDate && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">
            Selected: {formatDate(selectedDate)}
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomerDatePicker;